// client/src/components/MerchantPhotoViewer.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../CSS/PhotoViewer.css';
import { firestore, storage, storageFallback, getDownloadURLSmart } from '../../firebase';
import {
  doc,
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  limit,
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  ref,
  deleteObject,
  listAll,
  updateMetadata,
  getMetadata,
} from 'firebase/storage';
import { FaDownload, FaTrash, FaArrowLeft } from 'react-icons/fa';
import { useDataContext } from '../../DataContext';

/* ===== Helpers & sanitation ===== */
const sanitizeBasic = (s) => String(s ?? '').replace(/(['";]|--)/g, '');
const toUpperSlug = (s) => sanitizeBasic(String(s || '').toUpperCase().replace(/\s+/g, '_'));
const getFullPathFromUrl = (url) => {
  const m = String(url || '').match(/\/o\/(.*?)\?/);
  return m ? decodeURIComponent(m[1]) : null;
};

/* ====== Titre défaut (UX) ====== */
const DEFAULT_TITLES = new Set(['AUCUN TITRE', 'SANS TITRE', 'UNTITLED', 'NO TITLE', 'N/A']);
const isDefaultTitle = (t) => {
  const norm = String(t || '').trim();
  return !norm || DEFAULT_TITLES.has(norm.toUpperCase());
};
const normalizeTitle = (t) => sanitizeBasic(String(t || '').trim());

const MerchantPhotoViewer = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { photo } = state || {};

  // eslint-disable-next-line
  const [user, setUser] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photoTitle, setPhotoTitle] = useState(''); // affichage (RO)
  const [draftTitle, setDraftTitle] = useState(''); // input (RW)
  const [isEditing, setIsEditing] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);

  const downloadBusyRef = useRef(false);
  const deleteBusyRef = useRef(false);
  const saveTitleBusyRef = useRef(false);

  const { fetchData } = useDataContext();

  /* ====== Récupération robuste des photos (vos + partagées) ====== */
  const fetchPhotos = useMemo(
    () => async () => {
      try {
        const auth = getAuth();
        const userId = auth.currentUser?.uid;
        const userEmail = auth.currentUser?.email;
        if (!userId || !userEmail) return;

        // 1) cache local immédiat
        const cached = localStorage.getItem('userPhotos');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed)) setPhotos(parsed);
          } catch {}
        }

        // 2) UIDs employés
        const entrepriseRef = collection(firestore, 'entreprises');
        // Firestore rules: list on /entreprises requires a limit <= 50
        const snaps = await getDocs(
          query(entrepriseRef, where('createdBy', '==', userEmail), limit(50))
        );
        const employeeUids = [];
        snaps.forEach((d) => {
          const dat = d.data() || {};
          // defensive: nullish checks; only collect invitations when createdBy matches
          if (dat?.createdBy === userEmail && Array.isArray(dat.invitations)) {
            dat.invitations.forEach((inv) => {
              if (inv?.employeeUid) employeeUids.push(inv.employeeUid);
            });
          }
        });

        // 3) utilitaire de listage avec tolérance d’erreur
        const loadList = async (basePath, filterFn = () => true) => {
          let list;
          try {
            list = await listAll(ref(storage, basePath));
          } catch (err) {
            // Si le dossier n'existe pas sur le bucket principal, on tente le fallback
            if (storageFallback) {
              try {
                list = await listAll(ref(storageFallback, basePath));
              } catch {
                return [];
              }
            } else {
              return [];
            }
          }
          const result = [];
          for (const item of list.items) {
            try {
              // item appartient peut-être au bucket fallback ; pour rester robuste,
              // on utilise getDownloadURLSmart sur le chemin complet.
              const [meta, url] = await Promise.all([
                getMetadata(item),
                getDownloadURLSmart(item.fullPath || item)
              ]);
              const created = meta.timeCreated ? new Date(meta.timeCreated) : new Date();
              const cm = meta.customMetadata || {};
              result.push({
                name: item.name,
                url,
                timestamp: created.getTime(),
                date: created.toLocaleString('fr-FR', {
                  weekday: 'short',
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                }),
                employeePseudo: cm.TakenByPseudo || (basePath.startsWith(userId) ? 'Moi' : 'Inconnu'),
                metadata: cm,
                path: item.fullPath, // clé fiable pour actions
              });
            } catch {
              // ignore élément isolé
            }
          }
          return result.filter(filterFn);
        };

        const myPhotos = await loadList(`${userId}/photos/`);
        const sharedBatches = await Promise.all(
          employeeUids.map((uid) => loadList(`${uid}/photos/`, (p) => p?.metadata?.category === userId))
        );
        const all = [...myPhotos, ...sharedBatches.flat()].sort((a, b) => b.timestamp - a.timestamp);

        setPhotos(all);
        localStorage.setItem('userPhotos', JSON.stringify(all));
      } catch (e) {
        // Pas d’alert ici pour éviter le spam, on garde un fallback UI
        // eslint-disable-next-line no-console
        console.error('fetchPhotos failed:', e);
      }
    },
    []
  );

  /* ====== Auth + écoute user + data ====== */
  useEffect(() => {
    const auth = getAuth();
    const unsubAuth = onAuthStateChanged(auth, (u) => {
      if (!u) return navigate('/logingeneral');
      const userDoc = doc(firestore, 'users', u.uid);
      const unsubUser = onSnapshot(userDoc, (snap) => {
        const ud = snap.data() || {};
        setUser({ ...ud, pseudo: ud?.pseudo });

        fetchPhotos();
        fetchData('merchantInvoices')
          .then((d) => {
            if (Array.isArray(d) && d.length) {
              localStorage.setItem('merchantInvoices', JSON.stringify(d));
            }
          })
          .catch(() => {});
      });
      return () => unsubUser();
    });
    return () => unsubAuth();
  }, [navigate, fetchPhotos, fetchData]);

  /* ====== Résolution de la photo courante ======
     On privilégie la correspondance par fullPath, sinon on retombe sur name. */
  const currentPhoto = useMemo(() => {
    if (!photo) return null;

    // Essayez via URL → fullPath
    const pathFromUrl = getFullPathFromUrl(photo.url);
    if (pathFromUrl) {
      const byPath = photos.find((p) => p.path === pathFromUrl);
      if (byPath) return byPath;
    }
    // Sinon par nom (moins sûr mais OK si noms uniques)
    return photos.find((p) => p.name === photo.name) || photo;
  }, [photo, photos]);

  /* ====== Synchro titre (localStorage → metadata → UI) ====== */
  useEffect(() => {
    if (!currentPhoto || isEditing) return;
    // 1) cache local
    const local = (() => {
      try {
        const raw = localStorage.getItem('userPhotos');
        if (!raw) return null;
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr)) return null;
        const hitByPath =
          currentPhoto.path && arr.find((p) => p.path === currentPhoto.path);
        const hit = hitByPath || arr.find((p) => p.name === currentPhoto.name);
        return hit || null;
      } catch {
        return null;
      }
    })();

    const localTitle =
      local?.metadata?.title || local?.metadata?.customMetadata?.title;
    const metaTitle =
      currentPhoto?.metadata?.title || currentPhoto?.metadata?.customMetadata?.title;

    // Préfère toujours la valeur la plus fraîche (metadata en mémoire), puis éventuel cache local.
    if (metaTitle) {
      setPhotoTitle(metaTitle);
      return;
    }
    if (localTitle) {
      setPhotoTitle(localTitle);
      return;
    }

    // 2) lecture Storage si pas trouvé en local
    const path = currentPhoto.path || getFullPathFromUrl(currentPhoto.url);
    if (!path) return;

    (async () => {
      try {
        const meta = await getMetadata(ref(storage, path));
        const t = meta?.customMetadata?.title;
        if (t && t !== photoTitle) setPhotoTitle(t);
      } catch {
        // ignore
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPhoto, isEditing]);

  /* ====== Actions ====== */
  const handleBackClick = () => navigate('/merchantinvoices');

  const handleDownload = async (p) => {
    if (downloadBusyRef.current) return;
    downloadBusyRef.current = true;
    try {
      const res = await fetch(p.url);
      const blob = await res.blob();
      const md = p.metadata || {};
      const parts = [
        p.timestamp ? new Date(p.timestamp).toLocaleDateString('fr-FR').replace(/\//g, '-') : null,
        md.title ? toUpperSlug(md.title) : null,
        md.TakenByPseudo ? toUpperSlug(md.TakenByPseudo) : null,
      ].filter(Boolean);
      const fileName = parts.length ? `${parts.join('-')}.png` : 'FACTURE.png';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('download error:', e);
    } finally {
      downloadBusyRef.current = false;
    }
  };

  const handleDelete = async (p) => {
    if (deleteBusyRef.current) return;
    deleteBusyRef.current = true;
    try {
      const path =
        p.path || getFullPathFromUrl(p.url) || `${sanitizeBasic(p.metadata?.category)}/photos/${sanitizeBasic(p.name)}`;
      await deleteObject(ref(storage, path));

      const next = photos.filter((x) => x.path ? x.path !== path : x.name !== p.name);
      setPhotos(next);
      localStorage.setItem('userPhotos', JSON.stringify(next));

      navigate('/merchantinvoices', { replace: true });
    } catch (error) {
      if (error?.code === 'storage/object-not-found') {
        alert("Cette photo n'existe plus.");
      } else {
        alert('Erreur lors de la suppression de la photo.');
      }
      // eslint-disable-next-line no-console
      console.error('delete error:', error);
    } finally {
      deleteBusyRef.current = false;
    }
  };

  const saveTitleToMetadata = async (p, title) => {
    if (saveTitleBusyRef.current) return;
    saveTitleBusyRef.current = true;

    try {
      const clean = normalizeTitle(title);
      const path = p.path || getFullPathFromUrl(p.url);
      if (!path) return;
      const r = ref(storage, path);
      const meta = await getMetadata(r);
      await updateMetadata(r, {
        customMetadata: {
          ...(meta.customMetadata || {}),
          title: clean,
        },
      });

      // met à jour cache local
      const list = (() => {
        try {
          const raw = localStorage.getItem('userPhotos');
          const arr = raw ? JSON.parse(raw) : [];
          if (!Array.isArray(arr)) return [];
          return arr;
        } catch {
          return [];
        }
      })();

      const updated = list.map((x) =>
        (x.path && path ? x.path === path : x.name === p.name)
          ? {
              ...x,
              metadata: {
                ...(x.metadata || {}),
                title: clean,
                customMetadata: { ...(x.metadata?.customMetadata || {}), title: clean },
              },
            }
          : x
      );
      localStorage.setItem('userPhotos', JSON.stringify(updated));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('save title error:', e);
    } finally {
      saveTitleBusyRef.current = false;
    }
  };

  const commitTitleAndExit = () => {
    if (!currentPhoto) return;
    const clean = normalizeTitle(draftTitle);

    // si identique, on quitte juste
    const currTitle =
      currentPhoto?.metadata?.customMetadata?.title ||
      currentPhoto?.metadata?.title ||
      photoTitle ||
      '';
    if (normalizeTitle(currTitle) === clean) {
      setIsEditing(false);
      return;
    }

    saveTitleToMetadata(currentPhoto, clean);

    // met à jour state local
    const meta = currentPhoto.metadata || {};
    const custom = meta.customMetadata || {};
    const updated = {
      ...currentPhoto,
      metadata: { ...meta, title: clean, customMetadata: { ...custom, title: clean } },
    };
    // remplace la photo dans le tableau si elle y est
    setPhotos((prev) =>
      prev.map((x) => (x.path && updated.path ? x.path === updated.path : x.name === updated.name) ? updated : x)
    );
    setPhotoTitle(clean);
    setIsEditing(false);
  };

  /* ====== Guard ====== */
  if (!photo) return (
    <div className="eb-screen">
      <div className="eb-screen__panel">
        <p className="eb-screen__text">Aucune photo à afficher.</p>
      </div>
    </div>
  );
  if (!currentPhoto) {
    // on garde un affichage minimal avec les props reçues
    // et la possibilité de retourner
    return (
      <div className="photo-viewer-wrapper pvw-root">
        <div className="pvw-bar">
          <button onClick={() => navigate('/merchantinvoices')} className="back-button pvw-btn ghost">
            <FaArrowLeft className="back-icon" /> <span>Retour</span>
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 40 }}>
          <span className="eb-spinner" aria-hidden="true" />
          <p className="eb-muted" style={{ margin: 0, fontSize: 14 }}>Chargement de la photo…</p>
        </div>
      </div>
    );
  }

  /* ====== Styles image ====== */
  const baseImageStyle = {
    objectFit: 'contain',
    borderRadius: 'var(--eb-radius)',
    boxShadow: 'var(--eb-shadow-md)',
  };
  const landscapeStyle = { width: '100%', height: 'auto', maxWidth: '900px', maxHeight: '85vh' };
  const portraitStyle = { width: 'auto', height: 'auto', maxWidth: 'min(700px, 90vw)', maxHeight: '70vh' };

  return (
    <div className="photo-viewer-wrapper pvw-root">
      {/* Barre top */}
      <div className="pvw-bar">
        <button onClick={handleBackClick} className="back-button pvw-btn ghost">
          <FaArrowLeft className="back-icon" /> <span>Retour</span>
        </button>

        <button
          className="pvw-btn primary icon-only"
          onClick={() => handleDownload(currentPhoto)}
          title="Télécharger"
          aria-label="Télécharger"
        >
          <FaDownload className="icon" />
        </button>
      </div>

      {/* Titre (édition inline) */}
      <div className="photo-title-container pvw-title">
        {!isEditing ? (
          <span
            onClick={() => {
              setIsEditing(true);
              setDraftTitle(isDefaultTitle(photoTitle) ? '' : photoTitle);
            }}
            className="photo-title-text pvw-title-text"
            style={{
              color: isDefaultTitle(photoTitle) ? 'var(--eb-subtle)' : 'inherit',
            }}
            title="Cliquer pour modifier"
          >
            {isDefaultTitle(photoTitle) ? 'Ajouter un titre' : photoTitle}
          </span>
        ) : (
          <input
            type="text"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitTitleAndExit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitTitleAndExit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setIsEditing(false);
                setDraftTitle('');
              }
            }}
            autoFocus
            className="photo-title-input pvw-title-input"
            placeholder="Saisir un titre…"
          />
        )}
      </div>

      {/* Date / Employé */}
      <p className="pvw-date-center">
        {currentPhoto.timestamp
          ? new Date(currentPhoto.timestamp).toLocaleString('fr-FR', {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })
          : currentPhoto.date || ''}
        {currentPhoto.employeePseudo ? ` — ${currentPhoto.employeePseudo}` : ''}
      </p>

      {/* Image */}
      <div className="photo-content pvw-image-wrap">
        <img
          src={currentPhoto.url}
          alt={currentPhoto.name || 'Image affichée'}
          className="pvw-image"
          onLoad={(e) => {
            const { naturalWidth, naturalHeight } = e.currentTarget;
            setIsPortrait(naturalHeight > naturalWidth);
          }}
          style={{ ...(isPortrait ? portraitStyle : landscapeStyle), ...baseImageStyle }}
        />
      </div>

      {/* Supprimer */}
      <div className="delete-container pvw-actions">
        <button
          className="delete-button pvw-btn danger"
          onClick={() => {
            if (window.confirm('Êtes-vous sûr de vouloir supprimer cette photo ?')) {
              handleDelete(currentPhoto);
            }
          }}
          title="Supprimer la photo"
        >
          <FaTrash className="icon" /> <span>Supprimer</span>
        </button>
      </div>

      {/* Styles (scopés au viewer) */}
      <style>{`
          .pvw-root{
            min-height:100vh;
            min-height:100dvh;
            box-sizing:border-box;
            padding:24px 16px 56px;
            background:var(--eb-bg);
            color:var(--eb-text);
            display:flex;
            flex-direction:column;
            align-items:center;
          }
          .pvw-root .pvw-bar{
            width:100%;
            max-width:1000px;
            display:flex;
            align-items:center;
            justify-content:space-between;
            gap:12px;
            margin:0 0 16px;
          }
          .pvw-root .pvw-title{
            width:100%;
            max-width:1000px;
            display:flex;
            justify-content:center;
            align-items:center;
            margin:8px 0 4px;
          }
          .pvw-root .pvw-title-text{
            font-weight:700;
            letter-spacing:-0.01em;
            font-size:20px;
            padding:8px 12px;
            border-radius:var(--eb-radius-sm);
            border:1px dashed transparent;
            background:transparent;
            box-shadow:none;
            cursor:text;
            user-select:none;
            text-align:center;
            overflow-wrap:anywhere;
            transition:border-color .15s ease, background-color .15s ease;
          }
          .pvw-root .pvw-title-text:hover{ border-color:var(--eb-border-strong); background:var(--eb-surface); }
          .pvw-root .pvw-title-input{
            width:min(560px, 100%);
            min-height:46px;
            padding:0 14px;
            border-radius:var(--eb-radius-sm);
            border:1px solid var(--eb-brand-500);
            outline:none;
            box-shadow:var(--eb-ring);
            background:var(--eb-surface);
            font-size:17px;
            font-weight:600;
            text-align:center;
          }
          .pvw-root .pvw-date-center{
            width:100%;
            max-width:1000px;
            text-align:center;
            margin:0 0 20px;
            color:var(--eb-muted);
            font-size:14px;
            font-weight:500;
          }
          .pvw-root .pvw-image-wrap{
            width:100%;
            max-width:1000px;
            display:flex;
            justify-content:center;
            align-items:center;
            padding:16px;
            background:var(--eb-surface);
            border:1px solid var(--eb-border);
            border-radius:var(--eb-radius-lg);
            box-shadow:var(--eb-shadow-sm);
          }
          .pvw-root .pvw-image{ max-width:100% !important; }
          .pvw-root .pvw-actions{
            width:100%;
            max-width:1000px;
            display:flex;
            justify-content:center;
            margin-top:20px;
          }
          .pvw-root .pvw-btn{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            gap:8px;
            min-height:42px;
            padding:0 16px;
            border-radius:var(--eb-radius-sm);
            border:1px solid transparent;
            font-size:14.5px;
            font-weight:600;
            cursor:pointer;
            user-select:none;
            transition:background-color .15s ease, border-color .15s ease, color .15s ease;
          }
          .pvw-root .pvw-btn:focus-visible{ outline:none; box-shadow:var(--eb-ring); }
          .pvw-root .pvw-btn.icon-only{ width:42px; padding:0; }
          .pvw-root .pvw-btn.primary{ background:var(--eb-brand); color:#fff; box-shadow:var(--eb-shadow-xs); }
          .pvw-root .pvw-btn.primary:hover{ background:var(--eb-brand-700); }
          .pvw-root .pvw-btn.ghost{ background:transparent; color:var(--eb-brand); padding-left:8px; }
          .pvw-root .pvw-btn.ghost:hover{ background:var(--eb-brand-50); }
          .pvw-root .pvw-btn.danger{ background:var(--eb-surface); color:var(--eb-danger); border-color:#fbd0cb; box-shadow:var(--eb-shadow-xs); }
          .pvw-root .pvw-btn.danger:hover{ background:var(--eb-danger-bg); border-color:var(--eb-danger); }
          @media (max-width: 768px) {
            .pvw-root{ padding:16px 12px calc(var(--eb-bottom-nav-h) + 24px); }
            .pvw-root .pvw-image-wrap{ padding:8px; }
            .pvw-root .pvw-actions .pvw-btn{ width:100%; }
          }
      `}</style>
    </div>
  );
};

export default MerchantPhotoViewer;
